import React, { useEffect, useMemo, useState } from "react";
import {
  getTruckImage,
  cleanTruckLabel,
  findVehicle,
  capacityLabel,
  formatQuoteRands,
  formatTripKm,
  formatDriveDuration,
} from "./helpers";
import PromoRaffle from "./PromoRaffle";

// Generic body-type illustrations (in /public/body-types).
// Resized + WebP-compressed - ~3 KB each vs the original 1.1 MB PNGs.
const BODY_IMAGES = {
  open: "/body-types/open.webp",
  closed: "/body-types/closed.webp",
  fridge: "/body-types/fridge.webp",
  tipper: "/body-types/tipper.webp",
};

// Short descriptors to help customers pick the right body type.
const BODY_DESCRIPTIONS = {
  open: "Flatbed or dropside - load from the sides or top.",
  closed: "Fully enclosed - weatherproof and secure.",
  fridge: "Refrigerated - keeps goods cold in transit.",
  tipper: "Tips to offload sand, rubble and loose loads.",
};

const bodyKey = (label = "") => label.replace(/\s*body$/i, "").trim().toLowerCase();

/**
 * Body type is the primary filter (open / closed / fridge ...). Once a body
 * type is chosen, the list of vehicle sizes available in that body type is
 * shown below with an indicative "from" price. That figure is the lowest
 * possible for this route (distance + vehicle, no helpers/floors/extras),
 * using the same calculator as /quote, not a canned 2 km estimate.
 */
const VehiclePicker = ({
  vehicletypes,
  selected,
  open = true,
  onOpenChange,
  onSelect,
  addressesComplete = true,
  onNeedAddresses,
  tripPrices = {},
  tripDistanceMeters = 0,
  tripDurationSeconds = 0,
  collection,
  delivery,
  promocode = "",
  onApplyPromo,
  spinEnabled = false,
}) => {
  const vehiclesRef = React.useRef(null);
  const raffleRef = React.useRef(null);
  const headingRef = React.useRef(null);
  const [bodyScrollNonce, setBodyScrollNonce] = useState(0);
  const sellable = useMemo(
    () => (vehicletypes || []).filter((v) => v.sell),
    [vehicletypes]
  );

  const selectedVehicle = findVehicle(vehicletypes, selected);

  // The vehicle list only appears once a body type has been chosen.
  const [bodyChosen, setBodyChosen] = useState(false);
  const [activeBody, setActiveBody] = useState(null);

  // Distinct body types, in source order.
  const bodyTypes = useMemo(() => {
    const seen = new Set();
    const out = [];
    sellable.forEach((v) => {
      const label = v.bodylabel || "standard";
      if (!seen.has(label)) {
        seen.add(label);
        out.push(label);
      }
    });
    return out;
  }, [sellable]);

  // Keep the shown body type in sync when the selected vehicle changes
  // (e.g. picking from the "other options" list at the price view).
  // Clear body-type UI when selection is wiped (Start over).
  useEffect(() => {
    if (!selected) {
      setBodyChosen(false);
      setActiveBody(null);
      return;
    }
    setBodyChosen(true);
    const v = findVehicle(vehicletypes, selected);
    if (v?.bodylabel) setActiveBody(v.bodylabel);
  }, [selected, vehicletypes]);

  const effectiveBody = activeBody || selectedVehicle?.bodylabel || bodyTypes[0];

  // Vehicles in the active body type, one per load group.
  const vehiclesForBody = useMemo(() => {
    const seen = new Set();
    return sellable.filter((v) => {
      if ((v.bodylabel || "standard") !== effectiveBody) return false;
      const group = v.loadgroup || v.type;
      if (seen.has(group)) return false;
      seen.add(group);
      return true;
    });
  }, [sellable, effectiveBody]);

  const vehicleListCollapsed = !open && !!selectedVehicle;
  const showVehicleList = bodyChosen || bodyTypes.length <= 1;

  // Body-type click: bring Spin to win (and the vehicle list under it) to the
  // top of the viewport. If the raffle is flagged off, scroll the vehicle
  // heading instead. Do not use block:"nearest". Do not scroll after a spin win.
  useEffect(() => {
    if (!bodyScrollNonce || vehicleListCollapsed || !showVehicleList) {
      return undefined;
    }
    const node = spinEnabled ? raffleRef.current : headingRef.current;
    if (!node) return undefined;
    const id = window.requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [bodyScrollNonce, showVehicleList, vehicleListCollapsed, spinEnabled]);

  if (!sellable.length) {
    return <p className="lp2-help">Loading vehicles...</p>;
  }

  const tripKm = formatTripKm(tripDistanceMeters);
  const tripMins =
    tripDurationSeconds > 0
      ? formatDriveDuration(tripDurationSeconds / 60, { approximate: false })
      : "";
  const tripMeta = [tripKm, tripMins].filter(Boolean).join(" · ");

  const handleBodyChange = (label) => {
    // Require both addresses before letting them pick a body type / vehicle.
    if (!addressesComplete) {
      onNeedAddresses?.();
      return;
    }
    setActiveBody(label);
    setBodyChosen(true);
    setBodyScrollNonce((n) => n + 1);
    onOpenChange?.(true);
  };

  const renderVehicleSummary = () => {
    if (!selectedVehicle) return null;
    const name = selectedVehicle.loadgroup || cleanTruckLabel(selectedVehicle.type);
    const capacity = capacityLabel(selectedVehicle.tonnes);
    const body = (selectedVehicle.bodylabel || "").replace(/\s*body$/i, "").trim();
    const fromCents = tripPrices[selectedVehicle.type];
    const fromPrice = fromCents > 0 ? formatQuoteRands(fromCents) : "";
    return (
      <div className="lp2-vpicker-summary lp2-label--spaced">
        <img
          className="lp2-vpicker-summary-img"
          src={getTruckImage(selectedVehicle.type)}
          alt=""
          loading="lazy"
        />
        <div className="lp2-vpicker-summary-text">
          <span className="lp2-vpicker-summary-label">Selected vehicle</span>
          <span className="lp2-vpicker-summary-name">{name}</span>
          {(capacity || body || fromPrice) && (
            <span className="lp2-vpicker-summary-meta">
              {[
                capacity,
                body && `${body} body`,
                fromPrice && `from ${fromPrice}`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          )}
        </div>
        <button
          type="button"
          className="lp2-vpicker-summary-change"
          onClick={() => onOpenChange?.(true)}
        >
          <i className="mdi mdi-pencil-outline" aria-hidden="true" />
          Change
        </button>
      </div>
    );
  };

  return (
    <>
      {bodyTypes.length > 1 && (
        <>
          <span className="lp2-label">
            Body type<span className="lp2-req">*</span>
          </span>
          <div
            className="lp2-bodytypes"
            role="group"
            aria-label="Choose a body type"
          >
            {bodyTypes.map((label) => {
              const active =
                effectiveBody === label && (bodyChosen || !!selectedVehicle);
              const key = bodyKey(label);
              const img = BODY_IMAGES[key];
              const desc = BODY_DESCRIPTIONS[key];
              return (
                <button
                  type="button"
                  key={label}
                  className={"lp2-bodytype" + (active ? " is-selected" : "")}
                  aria-pressed={active}
                  onClick={() => handleBodyChange(label)}
                >
                  {img && (
                    <img
                      className="lp2-bodytype-img"
                      src={img}
                      alt=""
                      width={120}
                      height={80}
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                  <span className="lp2-bodytype-text">
                    <span className="lp2-bodytype-name">{key}</span>
                    {desc && <span className="lp2-bodytype-desc">{desc}</span>}
                  </span>
                  <i
                    className="mdi mdi-chevron-right lp2-bodytype-chevron"
                    aria-hidden="true"
                  />
                </button>
              );
            })}
          </div>
        </>
      )}

      {vehicleListCollapsed ? (
        renderVehicleSummary()
      ) : !showVehicleList ? (
        <p className="lp2-help lp2-label--spaced">
          Select a body type above to see available vehicles and prices.
        </p>
      ) : (
        <>
          {spinEnabled && (
            <div id="lp2-raffle" ref={raffleRef}>
              <PromoRaffle
                collection={collection}
                delivery={delivery}
                bodylabel={effectiveBody}
                truckType={selected || ""}
                promocode={promocode}
                onApply={(code) => {
                  onApplyPromo?.(code);
                  if (!code) return;
                  window.requestAnimationFrame(() => {
                    headingRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  });
                }}
              />
            </div>
          )}
          <div
            className="lp2-vpicker-heading lp2-label--spaced"
            id="lp2-choose-vehicle"
            ref={headingRef}
          >
            <span className="lp2-label">
              Choose your vehicle<span className="lp2-req">*</span>
            </span>
            {tripMeta && (
              <span className="lp2-vpicker-heading-km">
                <i className="mdi mdi-map-marker-distance" aria-hidden="true" />
                {tripMeta}
              </span>
            )}
          </div>
          <div
            className="lp2-vehicles"
            role="group"
            aria-label="Choose a vehicle"
            ref={vehiclesRef}
          >
            {vehiclesForBody.map((vehicle) => {
              const isSelected = selected === vehicle.type;
              const tripCents = tripPrices[vehicle.type];
              const tripPrice = tripCents > 0 ? formatQuoteRands(tripCents) : "";
              const capacity = capacityLabel(vehicle.tonnes);
              const examples = vehicle.tip?.examples;
              const like = vehicle.realWorldExamples?.[0];
              return (
                <button
                  type="button"
                  key={vehicle.type}
                  className={"lp2-vehicle" + (isSelected ? " is-selected" : "")}
                  aria-pressed={isSelected}
                  onClick={() => {
                    if (!addressesComplete) {
                      onNeedAddresses?.();
                      return;
                    }
                    onSelect(vehicle.type, { advance: true });
                  }}
                >
                  {isSelected && (
                    <i
                      className="mdi mdi-check-circle lp2-vehicle-check"
                      aria-hidden="true"
                    />
                  )}
                  <img
                    className="lp2-vehicle-img"
                    src={getTruckImage(vehicle.type)}
                    alt={`${vehicle.loadgroup || vehicle.type} with driver`}
                    loading="lazy"
                  />
                  <span className="lp2-vehicle-body">
                    <span className="lp2-vehicle-name">
                      {vehicle.loadgroup || cleanTruckLabel(vehicle.type)}
                      {capacity && (
                        <span className="lp2-vehicle-cap">{capacity}</span>
                      )}
                    </span>
                    {examples && (
                      <span className="lp2-vehicle-examples">
                        <i className="mdi mdi-lightbulb-on-outline" aria-hidden="true" />
                        {examples}
                      </span>
                    )}
                    {like && (
                      <span className="lp2-vehicle-like">e.g. {like}</span>
                    )}
                    <span className="lp2-vehicle-price">
                      {tripPrice ? (
                        <>
                          from {tripPrice}
                          <small> · driver included</small>
                        </>
                      ) : (
                        <small>Pricing your trip...</small>
                      )}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </>
  );
};

export default VehiclePicker;
